// @flow

import type { BasicProps } from '../../type'

import * as React from 'react'
import { appendPropsActions, appendPropsSchema } from '../util/schemaUtil'
import { getStore } from 'frame/core/store'
import { setRootData, isInEditor } from '../../redux/basic'
import {
  isInApp,
  getAppVersion,
  isInFjApp,
  isInWeixinApp,
  isInXqkdApp
} from 'frame/core/browserEnv'
import {
  toPageRouter,
  closeToRoot,
  close,
  openThirdPartyApps,
  nativeCopy
} from 'frame/core/app/all'
import { history } from 'redux/store'
import { alert } from 'frame/ui/popup'

type Props = BasicProps & {
  unLoginShow?: boolean,
  loginInvestShow?: boolean,
  loginNoInvestShow?: boolean,
  isShow?: boolean,
  visibleRule?: ?string,
  customProps?: any
};

type State = {
  isShow: boolean
};

export default function (WrappedComponent: React.ComponentType<*>) {
  class Component extends React.PureComponent<Props, State> {
    wrappedComponent: ?React.Component<*>;

    static schema = appendPropsSchema(WrappedComponent);

    static actions = appendPropsActions(WrappedComponent, [
      {
        title: '弹出警告框',
        name: 'alert',
        dataSchema: [
          {
            key: 'title',
            title: '标题(选填)'
          },
          {
            key: 'message',
            title: '内容',
            required: true
          },
          {
            key: 'btnText',
            title: '按钮文案(选填)'
          }
        ]
      },
      {
        title: '数据存储',
        name: 'saveToStore',
        dataSchema: [
          {
            type: 'select',
            key: 'storeType',
            title: '存储类型',
            options: [
              'memoryStore',
              'sessionStore',
              'localStore',
              'cookieStore'
            ],
            defaultValue: 'sessionStore'
          },
          {
            key: 'key',
            title: 'key',
            required: true
          },
          {
            title: '到期时间',
            key: 'expire',
            visible: value => value.storeType === 'cookieStore'
          }
        ]
      },
      {
        title: '绑定数据到 $root',
        name: 'bindRootData',
        dataSchema: [
          {
            key: 'namespace',
            title: '命名空间',
            required: true
          }
        ]
      },
      {
        title: '打印数据',
        name: 'logData'
      },
      {
        title: '原生复制(喜鹊快贷)',
        name: 'nativeCopy',
        dataSchema: [
          {
            key: 'nativeCopyText',
            title: '要复制的字符串',
            required: true
          }
        ]
      }
    ]);

    actionNativeCopy = (
      { nativeCopyText }: { nativeCopyText: string },
      dynamicData: any
    ) => {
      const { jsEnv } = this.props
      nativeCopyText = jsEnv.getStringWithAdditionCtx(
        nativeCopyText,
        dynamicData
      )
      nativeCopy(nativeCopyText)
    };

    actionAlert = (
      {
        title,
        message,
        btnText
      }: { title?: string, message: string, btnText: string },
      dynamicData: any
    ) => {
      const { jsEnv } = this.props
      title = title && jsEnv.getStringWithAdditionCtx(title, dynamicData)
      message = message && jsEnv.getStringWithAdditionCtx(message, dynamicData)
      btnText = btnText && jsEnv.getStringWithAdditionCtx(btnText, dynamicData)
      alert({ title, message, ok: btnText || undefined })
    };

    actionBindRootData = (data: { namespace: string }, dynamicData: any) => {
      const { namespace } = data
      setRootData(namespace, dynamicData)
    };

    actionSaveToStore = (
      {
        key,
        storeType,
        expire
      }: { key: string, storeType: string, expire?: string },
      dynamicData: any
    ) => {
      const { jsEnv } = this.props

      if (jsEnv && key) {
        key = jsEnv.getStringWithAdditionCtx(key, dynamicData)
      }
      if (jsEnv && expire) {
        expire = jsEnv.getStringWithAdditionCtx(expire, dynamicData)
      }
      getStore(storeType).set(key, dynamicData, expire)
    };

    actionLogData = (data: void, dynamicData: *) => {
      console.warn(dynamicData)
    };

    actionPushPage = (
      data: {
        h5: ?string,
        app: ?string,
        mpUrl: ?string,
        thirdPartyAppsUrl: ?string,
        openNewWebView: boolean,
        miniProOpenNewWebView: boolean,
        forceRefresh: boolean,
        replaceHistory: boolean,
        closeAppWebviewMode: 'closeToRoot' | 'close'
      },
      dynamicData: any
    ) => {
      const { jsEnv } = this.props
      const {
        h5,
        app,
        openNewWebView,
        miniProOpenNewWebView,
        forceRefresh,
        closeAppWebviewMode,
        replaceHistory,
        mpUrl,
        thirdPartyAppsUrl
      } = data
      const appUrl =
        isInApp() && !isInWeixinApp() && app
          ? jsEnv.getStringWithAdditionCtx(app, dynamicData)
          : ''
      const weixinAppUrl =
        isInWeixinApp() && mpUrl
          ? jsEnv.getStringWithAdditionCtx(mpUrl, dynamicData)
          : ''
      const thirdPartyAppUrl =
        isInXqkdApp() && thirdPartyAppsUrl
          ? jsEnv.getStringWithAdditionCtx(thirdPartyAppsUrl, dynamicData)
          : ''

      if (weixinAppUrl) {
        toPageRouter(weixinAppUrl)
      } else if (appUrl) {
        toPageRouter(appUrl)
        if (closeAppWebviewMode === 'close') {
          close()
        } else if (closeAppWebviewMode === 'closeToRoot') {
          closeToRoot()
        }
      } else if (isInApp() && closeAppWebviewMode) {
        if (closeAppWebviewMode === 'close') {
          close()
        } else if (closeAppWebviewMode === 'closeToRoot') {
          closeToRoot()
        }
      } else if (h5) {
        let url = this.parseUrl(h5, dynamicData)
        if (isInEditor()) {
          return alert({ title: '页面跳转', message: url })
        }
        let _openNewWebView = openNewWebView
        let isApp = isInApp()
        if (
          _openNewWebView &&
          (!isApp ||
            isInWeixinApp() ||
            (isInFjApp() && getAppVersion() <= '3.1.3'))
        ) {
          _openNewWebView = false
        }
        if (
          (forceRefresh ||
            _openNewWebView ||
            (miniProOpenNewWebView && isInWeixinApp())) &&
          !/^\w+:/.test(url)
        ) {
          url = window.location.origin + url
        }
        if (miniProOpenNewWebView && isInWeixinApp()) {
          toPageRouter(url)
          return
        }
        if (_openNewWebView && isApp && !isInWeixinApp()) {
          toPageRouter(url)
          return
        }
        if (!/^\w+:/.test(url)) {
          if (replaceHistory) {
            history.replace(url)
          } else {
            history.push(url)
          }
        } else {
          if (replaceHistory) {
            window.location.replace(url)
          } else {
            window.location.href = url
          }
        }
      } else if (thirdPartyAppUrl) {
        openThirdPartyApps(thirdPartyAppUrl)
      }
    };

    parseUrl = (url: string, dynamicData: any): string => {
      const { jsEnv } = this.props
      url = jsEnv.getStringWithAdditionCtx(url, dynamicData)
      url = url.trim()
      if (!this.urlIsCurrentProject(url) && !/^\w+:/.test(url)) {
        url = window.location.origin + url
      }
      return url
    };

    urlIsCurrentProject (url: string) {
      const pathName = window.location.pathname.match(/^(\/[^/]+\/[^/]+).*$/)
      const urlPathname = url.match(/^(\/[^/]+\/[^/]+).*$/)
      if (pathName && urlPathname && pathName[1] === urlPathname[1]) {
        return true
      }
      return false
    }

    render () {
      return (
        <WrappedComponent
          ref={ref => {
            this.wrappedComponent = ref
          }}
          actionPushPage={this.actionPushPage}
          parseUrl={this.parseUrl}
          {...this.props}
        />
      )
    }
  }

  return Component
}



// WEBPACK FOOTER //
// ./src/projects/easy/public/Comps/hoc/rootComp.js